import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

export const Insights = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles").then((res) => setArticles(res.data)).catch(() => {});
  }, []);

  if (articles.length === 0) return null;

  return (
    <section id="insights" data-testid="insights-section" className="border-t border-slate-200 bg-[#FAF9FF] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90">Insights</p>
            <h2 className="font-display mt-4 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              From the <span className="gold-text">research desk</span>
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-slate-500">
            Plain-language market notes on unlisted shares, Pre-IPO strategy and what the desk is watching.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2">
          {articles.map((a, i) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
            >
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`insight-card-${a.slug}`}
                className="card-glow group flex h-full flex-col overflow-hidden border border-slate-200 bg-[#F6F4FD]/70 backdrop-blur"
              >
                {a.cover_image && (
                  <div className="relative h-44 w-full overflow-hidden">
                    <img
                      data-testid={`insight-cover-${a.slug}`}
                      src={fileUrl(a.cover_image)}
                      alt={a.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#F6F4FD] via-transparent to-transparent" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-8">
                <div className="flex items-center justify-between">
                  <span className="border border-[#6D28D9]/30 bg-[#6D28D9]/[0.06] px-3 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#6D28D9]">
                    {a.category}
                  </span>
                  <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    <Clock className="h-3 w-3" /> {a.read_time}
                  </span>
                </div>
                <h3 className="font-display mt-6 text-xl font-medium leading-snug text-slate-900 transition-colors duration-300 group-hover:text-[#6D28D9] sm:text-2xl">
                  {a.title}
                </h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-500">{a.excerpt}</p>
                <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-5">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">{a.date}</span>
                  <span className="flex items-center gap-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6D28D9] transition-all duration-300 group-hover:gap-3">
                    Read Note <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
